import { ApplicationConfig, ErrorHandler, inject, provideAppInitializer, provideBrowserGlobalErrorListeners, Provider } from "@angular/core";
import { ConfirmationService, MessageService } from "primeng/api";
import { CustomConfirmationService } from "./services/custom-confirmation.service";
import { firstValueFrom } from "rxjs";
import { provideAnimations } from "@angular/platform-browser/animations";
import { INDEXED_DB_CONFIG, INDEXED_DB_VERSION } from "./core/storage/indexed-db.config";
import { LOGS_DB_CONFIG, LOGS_DB_NAME, LOGS_DB_VERSION, LOGS_STORE_NAME, LOGS_TIMESTAMP_INDEX_NAME } from "./core/storage/log-db.config";
import { provideRouter } from "@angular/router";
import { providePrimeNG } from "primeng/config";
import Aura from "@primeng/themes/aura";
import { routes } from "./app.routes";
import { ConfigService } from "./core/config/config.service";
import { APP_LANGUAGES } from "./core/localization/app-languages";
import { appOfflineInterceptor } from "./core/network/interceptors/app-offline.interceptor";
import { loggingInterceptor } from "./core/network/interceptors/logging.interceptor";
import { errorInterceptor } from "./core/network/interceptors/error.interceptor";
import { provideServiceWorker } from "@angular/service-worker";
import { OfflineManagerService } from "./core/offline/services/offline-manager.service";
import { PwaUpdateService } from "./core/pwa/pwa-update.service";
import {
  CC7_LOGGER,
  CC7_OFFLINE_READINESS,
  Cc7AuthConfigMutator,
  Cc7ConfigService,
  LocalizationService,
  provideCC7Auth,
  provideCC7Connector,
  withCc7UnauthorizedInterceptor,
} from "@rinami/cc7-ng";
import { GciinsOfflineReadinessChecker } from "./core/auth/gciins-offline-readiness-checker";
import { Logger } from "./core/logging/logger.service";
import { LogAggregatorClient } from "./core/logging/log-aggregator-client.service";
import { installConsoleInterceptor } from "./core/logging/console-interceptor";
import { AppErrorHandler } from "./core/logging/app-error-handler";
import { createCc7LoggerBridge } from "./core/logging/cc7-logger-bridge";
import { environment } from "../environments/environment";

const CUSTOM_CONFIRMATION_SERVICE_PROVIDER: Provider = {
  provide: ConfirmationService,
  useClass: CustomConfirmationService,
};

const xCantaraNamespace = environment.xCantaraNamespace;
if (!xCantaraNamespace) {
  throw new Error("xCantaraNamespace is not defined in environment configuration.");
}
const applicationName = environment.applicationName;
if (!applicationName) {
  throw new Error("applicationName is not defined in environment configuration.");
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideAnimations(),
    provideRouter(routes),
    MessageService,
    CUSTOM_CONFIRMATION_SERVICE_PROVIDER,
    provideCC7Connector({
      basePath: "",
      additionalInterceptors: [
        appOfflineInterceptor({
          ignoredEndpoints: ["/security/login", "/security/access", "/security/logout", "/security/session", "query/execute"],
        }),
        // Stays out of the way on auth routes (login page, SSO callback) on their own, so no SSO endpoints need ignoring here.
        // "/security/logout" stays ignored to avoid a logout loop.
        withCc7UnauthorizedInterceptor({ ignoredEndpoints: ["/security/login", "/security/logout"] }),
        loggingInterceptor,
        errorInterceptor({ ignoredEndpoints: ["/security/logout"] }),
      ],
      // Host-owned language list; the library only loads public/assets/i18n/<code>.json for these codes.
      // Registered here so localization is enabled before LocalizationService is first injected.
      localize: { languages: APP_LANGUAGES },
    }),
    // Runtime auth values are patched after the remote configuration request.
    provideCC7Auth({
      xCantaraNamespace: xCantaraNamespace,
      applicationName: applicationName,
      namespaceId: 0,
      ssoEnabled: false,
    }),
    provideAppInitializer(async () => {
      const cc7ConfigService = inject(Cc7ConfigService);
      const configService = inject(ConfigService);
      const localizationService = inject(LocalizationService);
      const authConfig = inject(Cc7AuthConfigMutator);
      const _om = inject(OfflineManagerService); // Inject to ensure it's initialized
      const _pwa = inject(PwaUpdateService); // Inject to start update polling
      const logAggregator = inject(LogAggregatorClient);
      // Patches console.* before application configuration resolves, so early startup logs are captured too.
      installConsoleInterceptor(inject(Logger));
      cc7ConfigService.configure({
        basePath: configService.hostName,
        maxConcurrentRequests: 1,
        maxConcurrentMediaRequests: 1,
        maxRetryAttempts: 2,
        // configure() replaces the whole configuration, so localize must be repeated or it resets to null.
        localize: { languages: APP_LANGUAGES },
      });
      await Promise.all([configService.loadConfig(), firstValueFrom(localizationService.loadTranslations())]);
      // Applied only after loadConfig() resolves; until then LogStoreWriter keeps its built-in defaults.
      logAggregator.configure({ maxEntries: configService.logMaxEntries, maxAgeDays: configService.logRetentionDays });
      // Patch the real auth config. These values also power cc7CantaraRequestInterceptor,
      // which enriches every outgoing request body (namespaceId/applicationName/etc.) and
      authConfig.set({
        applicationName: configService.applicationName,
        namespaceId: configService.namespaceId,
        environmentNames: configService.environments,
        cantaraVersion: configService.apiVersion,
        ssoEnabled: configService.isOAuthEnabled,
        ssoLoginUrl: configService.ssoLoginUrl ?? undefined,
      });
    }),
    // Routes CC7 diagnostics through the app's own Logger/gciins-logs-db pipeline, filtered by the
    // same logLevel as app logs — real console mirroring stays gated by logLevel === "DEBUG".
    { provide: CC7_LOGGER, useFactory: () => createCc7LoggerBridge(inject(Logger)) },
    { provide: CC7_OFFLINE_READINESS, useExisting: GciinsOfflineReadinessChecker },
    { provide: ErrorHandler, useClass: AppErrorHandler },
    providePrimeNG({
      theme: {
        preset: Aura,
        options: {
          darkModeSelector: ".dark",
        },
      },
      zIndex: {
        modal: 20100,
        overlay: 20000,
        menu: 20000,
        tooltip: 20100,
      },
    }),
    provideServiceWorker("ngsw-worker.js", {
      enabled: true,
      registrationStrategy: "registerWhenStable:30000",
    }),
    // Main app database, used by IndexedDbService. Wiped on logout and on PWA updates.
    {
      provide: INDEXED_DB_CONFIG,
      useValue: {
        dbName: "gciins-db",
        version: INDEXED_DB_VERSION,
        stores: [
          {
            name: "app-metadata",
            keyPath: "key",
          },
          {
            name: "equipment",
            keyPath: "uniqueKeyIDInternal",
            indexes: [
              { name: "asset_item_number_idx", keyPath: "assetItemNumber", unique: false },
              { name: "costCenter", keyPath: "costCenter", unique: false },
              { name: "parent_asset_item_number_idx", keyPath: "parentAssetItemNumber", unique: false },
              { name: "status", keyPath: "equipmentStatus", unique: false },
              { name: "unit_number_idx", keyPath: "unitNumber", unique: false },
            ],
          },
          {
            name: "work-orders",
            keyPath: ["orderNumber", "assetItemNumber"],
            indexes: [
              { name: "order_number_idx", keyPath: "orderNumber", unique: false },
              { name: "asset_number_idx", keyPath: "assetItemNumber", unique: false },
              { name: "unit_number_idx", keyPath: "unitNumber", unique: false },
            ],
          },
          {
            name: "offline-transactions",
            keyPath: "id",
          },
          {
            // Holds File/Blob data for queued MediaUpload transactions, kept out of
            // "offline-transactions" itself since that record is rewritten wholesale on every
            // status change — see `OfflineTransactionDao.save()` for why.
            name: "offline-transaction-files",
            keyPath: "transactionId",
          },
          {
            name: "offline-next-number",
            keyPath: "uniqueKey",
          },
          {
            name: "media-attachments",
            keyPath: ["objectName", "mediaKey", "sequenceNo"],
            indexes: [
              {
                name: "media_object_mediaKey_idx",
                keyPath: ["objectName", "mediaKey"],
                unique: false,
              },
            ],
          },
          {
            name: "checklist",
            keyPath: ["documentOrderInvoiceE", "assetItemNumber", "costCenter", "specification", "testID"],
          },
          {
            name: "userDefinedCode",
            keyPath: ["productCode", "recordType", "key"],
          },
          {
            name: "question-responses",
            keyPath: ["documentOrderInvoiceE", "assetItemNumber", "costCenter", "specification", "testID"],
          },
        ],
      },
    },
    // Logs database, used by LogStoreWriter. Kept through PWA updates, cleared on logout.
    {
      provide: LOGS_DB_CONFIG,
      useValue: {
        dbName: LOGS_DB_NAME,
        version: LOGS_DB_VERSION,
        stores: [
          {
            name: LOGS_STORE_NAME,
            keyPath: "id",
            indexes: [{ name: LOGS_TIMESTAMP_INDEX_NAME, keyPath: "timestamp", unique: false }],
          },
        ],
      },
    },
  ],
};
